import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import {
  BASE_TEXT_COLOR_VARIANTS,
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { DEFAULT_INITIAL_DATE, formatUtcSeconds } from '../../../../utils/dates';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { DARK_THEME_NAME } from '../../types/editor-settings';

interface DateInputProperties {
  text: string;
  style: string;
  selectedDateTime: string;
  captionWidth: number;
  inputType: string;
}

@Component({
  selector: 'lfe-date-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './date-input-item-form.component.html',
  standalone: true,
  styleUrl: './date-input-item-form.component.css'
})
export class DateInputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<DateInputProperties>))
  implements OnInit, IBaseItem<DateInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  readonly updateFirstColumnWidthRequired = output<{ id: string; width: number }>();

  readonly translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & DateInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    selectedDateTime: '',
    captionWidth: 0,
    inputType: 'date'
  };
  disabled = false;

  firstColumnWidth = computed(
    () => this.sharedFirstColumnWidths()[this.firstSiblingItemId()] || getOptimalFirstColumnWidth(this.properties.text)
  );

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `date_input_${this.formControlId}`);

  currentTextColor = signal('');

  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  inputClass = computed(() => {
    const isDarkMode = this.settings().theme === DARK_THEME_NAME;
    const useInvertedCalendarIndicator = isDarkMode
      ? this.currentInputTextColor() === BASE_TEXT_COLOR_VARIANTS.black
      : this.currentInputTextColor() === BASE_TEXT_COLOR_VARIANTS.white;
    return { 'inverted-calendar-indicator': useInvertedCalendarIndicator };
  });

  getTextStyle(): { [key: string]: string } | null {
    if (!this.properties.text) {
      return null;
    }

    const textStyle = getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor());

    return {
      'min-width': `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...textStyle
    };
  }

  getTextTitle(): string | null {
    if (!this.properties.text) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.text;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.parseStringToDateInput(this.properties.selectedDateTime);
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.selectedDateTime !== DEFAULT_INITIAL_DATE) {
      upsertChildren(element, { SelectedDateTime: this.properties.selectedDateTime });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & DateInputProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const selectedDateTime = props['SelectedDateTime'] || '';
    const captionWidth = Number(props['CaptionWidth'] || 0);
    const inputType = props['Type'] || 'date';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      textBgColor,
      inputColor,
      style,
      selectedDateTime,
      captionWidth,
      inputType,
      isGenericRepeatItem
    };
  }

  private parseStringToDateInput(value: string): string | null {
    if (!value || value === DEFAULT_INITIAL_DATE) {
      return null;
    }

    // Parse ISO date
    if (value.includes('-')) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : this.formatDateForInput(date);
    }

    // Parse dot separated dates (DD.MM.YYYY)
    const [day, month, year] = value.split('.');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return this.formatDateForInput(date);
  }

  /**
   * WORKAROUND - reads the local date fields, because the desktop app stores SelectedDateTime as local
   * midnight converted to real UTC: 07.09.2026 in UTC+2 is written as 2026-09-06T22:00:00Z. Using
   * toISOString() here returned the UTC day and showed the 6th.
   *
   * For a date-only field that conversion is arguably wrong - the same form shows a different day to a
   * colleague in another timezone. The correct behaviour is to keep the day verbatim as UTC midnight,
   * which is what toISOString() then reads back:
   *
   * return date.toISOString().split('T')[0];
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateForStorage(inputValue: string): string {
    if (!inputValue) return DEFAULT_INITIAL_DATE;

    const [year, month, day] = inputValue.split('-');
    return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
  }

  /**
   * WORKAROUND - counterpart of formatDateForInput: stores the picked day as LOCAL midnight converted
   * to real UTC, which is what the desktop app writes (07.09.2026 in UTC+2 becomes
   * 2026-09-06T22:00:00Z).
   *
   * The correct behaviour for a date-only field is UTC midnight, so the day cannot shift between
   * timezones:
   *
   * return new Date(`${inputValue}T00:00:00Z`).toISOString();
   */
  private toSelectedDateTime(inputValue: string): string {
    const [year, month, day] = inputValue.split('-').map(Number);
    return formatUtcSeconds(new Date(year, month - 1, day, 0, 0, 0, 0));
  }

  onSetTodayClick(event: Event): void {
    event.preventDefault();
    const todayString = this.formatDateForInput(new Date());
    this.formControl?.setValue(todayString);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    // Also update the properties for XML storage
    this.properties.selectedDateTime = this.toSelectedDateTime(todayString);
    this.properties.resultTime = formatUtcSeconds(new Date());
    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.formatDateForStorage(todayString);
  }

  onClearDateClick(event: Event): void {
    event.preventDefault();
    this.formControl?.setValue(null);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    // Also update the properties for XML storage
    this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    this.properties.resultTime = formatUtcSeconds(new Date());
    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.properties.selectedDateTime;
  }

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value) {
      // Update the underlying selectedDateTime format for XML storage
      this.properties.selectedDateTime = this.toSelectedDateTime(value);
      this.properties.resultTime = formatUtcSeconds(new Date());
      this.properties.resultUser = this.user().displayName;
      this.properties.resultValue = this.formatDateForStorage(value);
    } else {
      this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    }
  }
}
